import { useState } from 'react'
import { Calendar, ChevronDown, FileQuestion } from 'lucide-react'
import { useExamsList, useExamSummary } from '../../../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../../../stores/pyqAnalyticsStore'
import LoadingSection from '../shared/LoadingSection'
import ErrorSection from '../shared/ErrorSection'
import { DIFFICULTY_COLORS } from '../../../constants/colors'
import type { DifficultyLevel } from '../../../types'

export default function ExamHistorySection() {
  const { yearRangeStart, yearRangeEnd } = usePYQAnalyticsStore()
  const [selectedTestCode, setSelectedTestCode] = useState<string | null>(null)

  const { data: examsList, isLoading, isError, refetch } = useExamsList({
    yearRangeStart,
    yearRangeEnd,
  })

  const { data: examSummary, isLoading: isSummaryLoading } = useExamSummary(selectedTestCode)

  if (isLoading) {
    return <LoadingSection title="Exam History" height="h-96" />
  }

  if (isError) {
    return <ErrorSection title="Failed to load exams" onRetry={() => refetch()} height="h-96" />
  }

  if (!examsList) return null

  const exams = examsList.exams || []

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Past Exams Difficulty Distribution</h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exam Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Exam</label>
          <div className="relative">
            <select
              value={selectedTestCode || ''}
              onChange={(e) => setSelectedTestCode(e.target.value || null)}
              className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Choose an exam to view details...</option>
              {exams.map((exam) => (
                <option key={exam.testCode} value={exam.testCode}>
                  {exam.examName} ({exam.year}) - {exam.totalQuestions} questions
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>

          {/* Exam List */}
          <div className="mt-4 max-h-64 overflow-y-auto space-y-2">
            {exams.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                No exams found in the selected year range
              </p>
            ) : (
              exams.map((exam) => (
                <button
                  key={exam.testCode}
                  onClick={() => setSelectedTestCode(exam.testCode)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                    selectedTestCode === exam.testCode
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{exam.examName}</p>
                      <p className="text-xs text-gray-500">Year: {exam.year}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{exam.totalQuestions}</p>
                    <p className="text-xs text-gray-500">questions</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Exam Details */}
        <div>
          {!selectedTestCode ? (
            <div className="h-full flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <div className="text-center p-6">
                <FileQuestion className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">Select an exam</p>
                <p className="text-sm text-gray-500">
                  Choose an exam from the list to view its analysis
                </p>
              </div>
            </div>
          ) : isSummaryLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="animate-pulse text-gray-500">Loading exam details...</div>
            </div>
          ) : examSummary ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900">{examSummary.examName}</h4>
                <p className="text-sm text-gray-500">
                  Year: {examSummary.year} • {examSummary.totalQuestions} questions •{' '}
                  {examSummary.topicsCovered} topics
                </p>
              </div>

              {/* Difficulty Distribution */}
              {examSummary.difficultyDistribution && examSummary.difficultyDistribution.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Difficulty Distribution</p>
                  <div className="space-y-2">
                    {examSummary.difficultyDistribution.map((diff) => (
                      <div key={diff.name} className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-16">{diff.name || 'Unknown'}</span>
                        <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${diff.percentage ?? 0}%`,
                              backgroundColor: DIFFICULTY_COLORS[diff.name as DifficultyLevel] || '#6B7280',
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-16 text-right">
                          {diff.value ?? 0} ({(diff.percentage ?? 0).toFixed(0)}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
